import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import type { MediaItem } from "@/types/media";

interface HeroCarouselProps {
  items: MediaItem[];
}

export default function HeroCarousel({ items }: HeroCarouselProps) {
  const [active, setActive] = useState(0);

  useEffect(() => {
    if (items.length <= 1) return;
    const timer = window.setInterval(() => {
      setActive((prev) => (prev + 1) % items.length);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [items.length]);

  if (items.length === 0) return null;

  const current = items[active];
  const typeLabel = current.type === "movie" ? "电影" : "剧集";

  const goPrev = () => setActive((prev) => (prev - 1 + items.length) % items.length);
  const goNext = () => setActive((prev) => (prev + 1) % items.length);

  return (
    <section className="relative w-full overflow-hidden bg-primary-950">
      <div className="absolute inset-0">
        <img
          key={current.id}
          src={current.backdrop || current.poster}
          alt={`${current.title}焦点图`}
          title={`${current.title} - 蘑菇MOGU2影视详情`}
          className="h-full w-full animate-fade-in object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-primary-950/92 via-primary-950/70 to-primary-950/40"></div>
        <div className="absolute inset-0 bg-gradient-to-t from-primary-950/80 via-transparent to-primary-950/30"></div>
      </div>

      <div className="relative mx-auto flex h-[520px] w-full max-w-[1280px] flex-col justify-end px-4 pb-14 pt-16 md:h-[600px] md:px-8 md:pb-20">
        <h1 className="mb-4 text-sm font-medium tracking-widest text-background-200">
          蘑菇MOGU2 · 影视内容大全与最新资讯
        </h1>

        <div key={current.id} className="max-w-2xl animate-fade-up">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-500 px-3 py-1 text-xs font-bold text-primary-950">
            <i className="ri-fire-fill"></i>
            焦点推荐 · {typeLabel}
          </span>
          <h2 className="mt-4 text-3xl font-extrabold leading-tight text-white md:text-5xl">
            {current.title}
          </h2>
          <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-background-200">
            <span className="flex items-center gap-1 font-semibold text-accent-400">
              <i className="ri-star-fill"></i>
              {current.rating.toFixed(1)}
            </span>
            <span>{current.year}</span>
            <span>{current.region}</span>
            <span>{current.duration}</span>
            <span className="hidden sm:inline">{current.genres.join(" / ")}</span>
          </div>
          <p className="mt-4 line-clamp-2 max-w-xl text-sm leading-relaxed text-background-200 md:text-base">
            {current.summary}
          </p>
          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={`/detail/${current.id}`}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-semibold text-primary-950 transition-colors hover:bg-accent-400"
            >
              <i className="ri-play-fill text-base"></i>
              查看详情
            </Link>
            <Link
              to={`/category/${current.type}`}
              className="flex cursor-pointer items-center gap-2 whitespace-nowrap rounded-md border border-white/30 px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-white/10"
            >
              更多{typeLabel}
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>
        </div>

        <div className="mt-8 flex items-center gap-3">
          <button
            type="button"
            aria-label="上一部"
            onClick={goPrev}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-white/30 text-white transition-colors hover:bg-white/10"
          >
            <i className="ri-arrow-left-s-line text-xl"></i>
          </button>
          <button
            type="button"
            aria-label="下一部"
            onClick={goNext}
            className="flex h-9 w-9 cursor-pointer items-center justify-center rounded-full border border-white/30 text-white transition-colors hover:bg-white/10"
          >
            <i className="ri-arrow-right-s-line text-xl"></i>
          </button>
          <div className="ml-2 flex items-center gap-2">
            {items.map((item, index) => (
              <button
                key={item.id}
                type="button"
                aria-label={`切换到${item.title}`}
                onClick={() => setActive(index)}
                className={`h-1.5 cursor-pointer rounded-full transition-all ${
                  index === active ? "w-7 bg-accent-500" : "w-3 bg-white/40 hover:bg-white/70"
                }`}
              ></button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}